import { Box, Button, Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import LoadingButton from '../Common/LoadingButton';
import {
  deleteCalendar,
  resetCalendarAuthURI,
  resetCheckTokenExists,
} from '../../redux/booking/bookingSlice';
import { TokenTypeEnum } from '../../types/booking';
import { useAppSelector } from '../../redux/hooks';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

const StyledBackButton = styled(Button)(({ disabled }) => ({
  borderRadius: '10px',
  border: !disabled
    ? '1.5px solid var(--Brand-Cadet-Blue, #6599A5)'
    : '1.5px solid var(--Accent-Light-Steel-Blue, #EEE)',
  '& span': {
    background: !disabled
      ? 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)'
      : 'var(--Accent-Light-Steel-Blue, #EEE)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'Satoshi',
    fontSize: '15px',
    fontWeight: 700,
    lineHeight: '20px',
  },
  '&:hover': {
    border: '1.5px solid var(--Brand-Cadet-Blue, #6599A5)',
  },
}));

type ICalendarOptionProps = {
  id?: number;
  icon: string;
  text: string;
  selectedOption: number | null;
  onSelect: (_id: number | null) => void;
  disableIcon?: string;
  loading?: boolean;
  deleteLoading?: boolean;
  deleteSuccess?: boolean;
  email: string | null;
};

function CalendarOption({
  icon,
  text,
  selectedOption,
  onSelect,
  id,
  disableIcon,
  loading,
  deleteLoading,
  deleteSuccess,
  email,
}: ICalendarOptionProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isTokenExists } = useAppSelector((state) => state.booking);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  useEffect(() => {
    if (deleteSuccess) {
      onSelect(null);
      dispatch(resetCheckTokenExists());
      dispatch(resetCalendarAuthURI());
    }
  }, [deleteSuccess]);

  const handleChangeCalendar = () => {
    if (selectedOption === null) return;
    dispatch(
      deleteCalendar({
        calenderType:
          selectedOption === 1 ? TokenTypeEnum.GOOGLE_CALENDER : TokenTypeEnum.MICROSOFT_CALENDAR,
        isConfigured: true,
      })
    );
  };
  return (
    <Grid
      p="16px 24px"
      borderRadius="10px"
      border={
        selectedOption === null || selectedOption === id
          ? '1px solid var(--Accent-Unbleached-Silk, #FFDDC8)'
          : '1px solid var(--Accent-Light-Steel-Blue, #EEE)'
      }
      display="flex"
      flexDirection="row"
      width="100%"
      justifyContent="space-between"
      alignItems="center"
    >
      <Grid display="flex" alignItems="center" gap={3}>
        <img
          src={selectedOption === null || selectedOption === id ? icon : disableIcon}
          alt="logos-calendar"
        />
        <Grid display="flex" flexDirection="column" gap="12px">
          <Typography
            variant="bodyBold"
            color={selectedOption === null || selectedOption === id ? 'primary' : '#eee'}
          >
            {t(text)}
          </Typography>
          {selectedOption === id && isTokenExists && !deleteLoading && (
            <Typography
              variant="bodyRegular"
              color={selectedOption === null || selectedOption === id ? 'primary' : '#eee'}
            >
              {email}
            </Typography>
          )}
        </Grid>
      </Grid>
      {checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS) && (
        <Box>
          {deleteLoading && selectedOption === id && (
            <LoadingButton paddingX={12} paddingY={10}>
              {t('bookings.bookingSettings.wait')}
            </LoadingButton>
          )}
          {selectedOption === id && loading && !isTokenExists && (
            <LoadingButton paddingX={12} paddingY={10}>
              {t('bookings.linkCalendarStep.connecting')}
            </LoadingButton>
          )}
          {selectedOption === id && isTokenExists && !deleteLoading && (
            <StyledBackButton
              onClick={handleChangeCalendar}
              sx={{ padding: '0px 12px !important', height: '45px' }}
            >
              <span
                style={{
                  maxWidth: '240px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('bookings.bookingSettings.changeCalendar')}
              </span>
            </StyledBackButton>
          )}
          {(selectedOption === null || selectedOption === id) &&
            !loading &&
            !isTokenExists &&
            !deleteLoading && (
              <StyledBackButton
                sx={{ padding: '8px 12px !important' }}
                onClick={() => onSelect(id!)}
                disabled={!(selectedOption === null || selectedOption === id)}
              >
                <span>{t('bookings.linkCalendarStep.connect')}</span>
              </StyledBackButton>
            )}
        </Box>
      )}
    </Grid>
  );
}

export default CalendarOption;
